import React, { useEffect, useState } from 'react';
import { Search, Calendar, Users, BarChart3, ChevronDown, FilterIcon, TrendingUp, TrendingDown, BookOpenIcon, BarChart2Icon, BarChart, ChevronDownIcon } from 'lucide-react';
import ExamAnalysis from './ExamAnalysis';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const Reports = () => {
  // State for view selection
  const [currentView, setCurrentView] = useState<'general' | 'exam-analysis'>('general');
  // State for filters
  const [selectedClass, setSelectedClass] = useState('Tüm Sınıflar');
  const [selectedDate, setSelectedDate] = useState('Son 30 Gün');
  const [selectedExam, setSelectedExam] = useState('Tüm Denemeler');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStudent, setSearchStudent] = useState('');
  // New state for updated UI
  const [selectedScope, setSelectedScope] = useState('Kurum Geneli');
  const [showScopeDropdown, setShowScopeDropdown] = useState(false);
  const [showExamDropdown, setShowExamDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [selectedGradeLevel, setSelectedGradeLevel] = useState('8');
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 20;
  // Mock data
  const classes = ['Tüm Sınıflar', '9-A', '9-B', '10-A', '10-B', '11-A', '11-B', '12-A', '12-B'];
  const dateRanges = ['Son 30 Gün', 'Son 3 Ay', 'Son 6 Ay', 'Tüm Yıl'];
  // Exam data - reordered with most recent exams first
  const allExamsData = [{
    id: 'lgs8',
    name: 'LGS 8',
    date: '30.01.2024'
  }, {
    id: 'lgs7',
    name: 'LGS 7',
    date: '15.12.2023'
  }, {
    id: 'lgs6',
    name: 'LGS 6',
    date: '01.12.2023'
  }, {
    id: 'lgs5',
    name: 'LGS 5',
    date: '15.11.2023'
  }, {
    id: 'lgs4',
    name: 'LGS 4',
    date: '01.11.2023'
  }, {
    id: 'lgs3',
    name: 'LGS 3',
    date: '15.10.2023'
  }, {
    id: 'lgs2',
    name: 'LGS 2',
    date: '01.10.2023'
  }, {
    id: 'lgs1',
    name: 'LGS 1',
    date: '15.09.2023'
  }];
  // Create a mapping of exam IDs to dates for easy reference
  const examDates = allExamsData.reduce((acc, exam) => {
    acc[exam.id] = exam.date;
    return acc;
  }, {});
  // Should show grade dropdown only when "Kurum Geneli" is selected
  const shouldShowGradeDropdown = selectedScope === 'Kurum Geneli';
  // Handle scope change
  const handleScopeChange = newScope => {
    setSelectedScope(newScope);
    if (newScope !== 'Kurum Geneli') {
      // If specific class selected, automatically set grade level
      const autoGrade = newScope.charAt(0);
      setSelectedGradeLevel(autoGrade);
    }
    setShowScopeDropdown(false);
  };
  // Mock student data
  const [students, setStudents] = useState([{
    id: 1,
    name: 'Ahmet Yılmaz',
    net: 65,
    rank: 3,
    correctAnswers: 72,
    wrongAnswers: 18
  }, {
    id: 2,
    name: 'Ayşe Demir',
    net: 78,
    rank: 1,
    correctAnswers: 82,
    wrongAnswers: 8
  }, {
    id: 3,
    name: 'Mehmet Kaya',
    net: 70,
    rank: 2,
    correctAnswers: 77,
    wrongAnswers: 13
  }, {
    id: 4,
    name: 'Zeynep Çelik',
    net: 62,
    rank: 4,
    correctAnswers: 69,
    wrongAnswers: 21
  }, {
    id: 5,
    name: 'Can Öztürk',
    net: 58,
    rank: 5,
    correctAnswers: 65,
    wrongAnswers: 25
  }]);
  // Filtered students based on search
  const filteredStudents = students.filter(student => student.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Mock exam analysis data
  const [examAnalysis, setExamAnalysis] = useState({
    averageNet: 67,
    highestNet: 78,
    lowestNet: 58,
    participationRate: 92
  });
  // Mock class development data for the graph
  const [developmentData, setDevelopmentData] = useState([{
    month: 'Ocak',
    average: 55
  }, {
    month: 'Şubat',
    average: 58
  }, {
    month: 'Mart',
    average: 62
  }, {
    month: 'Nisan',
    average: 65
  }, {
    month: 'Mayıs',
    average: 70
  }]);
  // New mock data for updated UI
  const [scopeData, setScopeData] = useState({
    averageNet: 67,
    highestNet: 82,
    topStudent: 'Ayşe Demir',
    topStudentClass: '8-A',
    participationRate: 95,
    participantCount: 152,
    totalStudents: 160
  });
  // New development data for line chart
  const [newDevelopmentData, setNewDevelopmentData] = useState([{
    examName: 'LGS 1',
    average: 54,
    '8A': 56,
    '8B': 52,
    '8C': 53
  }, {
    examName: 'LGS 2',
    average: 57,
    '8A': 59,
    '8B': 55,
    '8C': 56
  }, {
    examName: 'LGS 3',
    average: 59,
    '8A': 62,
    '8B': 57,
    '8C': 58
  }, {
    examName: 'LGS 4',
    average: 62,
    '8A': 65,
    '8B': 60,
    '8C': 61
  }, {
    examName: 'LGS 5',
    average: 65,
    '8A': 68,
    '8B': 63,
    '8C': 64
  }, {
    examName: 'LGS 6',
    average: 67,
    '8A': 71,
    '8B': 65,
    '8C': 66
  }, {
    examName: 'LGS 7',
    average: 70,
    '8A': 73,
    '8B': 68,
    '8C': 69
  }, {
    examName: 'LGS 8',
    average: 72,
    '8A': 76,
    '8B': 70,
    '8C': 71
  }]);
  // Generate class-specific student data
  const get8AStudents = () => {
    const students = [];
    for (let i = 1; i <= 30; i++) {
      students.push({
        id: i,
        name: `Ahmet ${i}`,
        class: '8-A',
        totalNet: 90 - i * 0.3,
        mathNet: parseFloat((20 - i * 0.06).toFixed(1)),
        turkishNet: parseFloat((20 - i * 0.05).toFixed(1)),
        scienceNet: parseFloat((20 - i * 0.07).toFixed(1)),
        revolutionNet: parseFloat((10 - i * 0.03).toFixed(1)),
        religionNet: parseFloat((10 - i * 0.025).toFixed(1)),
        englishNet: parseFloat((10 - i * 0.035).toFixed(1))
      });
    }
    return students;
  };
  const get8BStudents = () => {
    const students = [];
    for (let i = 1; i <= 32; i++) {
      students.push({
        id: 100 + i,
        name: `Mehmet ${i}`,
        class: '8-B',
        totalNet: 88 - i * 0.25,
        mathNet: parseFloat((19 - i * 0.05).toFixed(1)),
        turkishNet: parseFloat((19 - i * 0.04).toFixed(1)),
        scienceNet: parseFloat((19 - i * 0.06).toFixed(1)),
        revolutionNet: parseFloat((10 - i * 0.028).toFixed(1)),
        religionNet: parseFloat((10 - i * 0.026).toFixed(1)),
        englishNet: parseFloat((11 - i * 0.032).toFixed(1))
      });
    }
    return students;
  };
  const get8CStudents = () => {
    const students = [];
    for (let i = 1; i <= 28; i++) {
      students.push({
        id: 200 + i,
        name: `Ayşe ${i}`,
        class: '8-C',
        totalNet: 89 - i * 0.28,
        mathNet: parseFloat((19.5 - i * 0.055).toFixed(1)),
        turkishNet: parseFloat((19.5 - i * 0.045).toFixed(1)),
        scienceNet: parseFloat((19.5 - i * 0.065).toFixed(1)),
        revolutionNet: parseFloat((10.2 - i * 0.03).toFixed(1)),
        religionNet: parseFloat((10.1 - i * 0.025).toFixed(1)),
        englishNet: parseFloat((10.2 - i * 0.033).toFixed(1))
      });
    }
    return students;
  };
  const get8DStudents = () => {
    const students = [];
    for (let i = 1; i <= 30; i++) {
      students.push({
        id: 300 + i,
        name: `Fatma ${i}`,
        class: '8-D',
        totalNet: 87 - i * 0.27,
        mathNet: parseFloat((19 - i * 0.052).toFixed(1)),
        turkishNet: parseFloat((19 - i * 0.048).toFixed(1)),
        scienceNet: parseFloat((19 - i * 0.062).toFixed(1)),
        revolutionNet: parseFloat((9.8 - i * 0.029).toFixed(1)),
        religionNet: parseFloat((9.9 - i * 0.027).toFixed(1)),
        englishNet: parseFloat((10.3 - i * 0.034).toFixed(1))
      });
    }
    return students;
  };
  const get7AStudents = () => {
    const students = [];
    for (let i = 1; i <= 30; i++) {
      students.push({
        id: 400 + i,
        name: `Zeynep ${i}`,
        class: '7-A',
        totalNet: 85 - i * 0.26,
        mathNet: parseFloat((18 - i * 0.05).toFixed(1)),
        turkishNet: parseFloat((18 - i * 0.045).toFixed(1)),
        scienceNet: parseFloat((18 - i * 0.06).toFixed(1)),
        revolutionNet: parseFloat((9.5 - i * 0.028).toFixed(1)),
        religionNet: parseFloat((10.5 - i * 0.029).toFixed(1)),
        englishNet: parseFloat((11 - i * 0.031).toFixed(1))
      });
    }
    return students;
  };
  const get7BStudents = () => {
    const students = [];
    for (let i = 1; i <= 32; i++) {
      students.push({
        id: 500 + i,
        name: `Ali ${i}`,
        class: '7-B',
        totalNet: 84 - i * 0.25,
        mathNet: parseFloat((18 - i * 0.048).toFixed(1)),
        turkishNet: parseFloat((18 - i * 0.043).toFixed(1)),
        scienceNet: parseFloat((18 - i * 0.058).toFixed(1)),
        revolutionNet: parseFloat((9.7 - i * 0.027).toFixed(1)),
        religionNet: parseFloat((10.2 - i * 0.024).toFixed(1)),
        englishNet: parseFloat((10.1 - i * 0.03).toFixed(1))
      });
    }
    return students;
  };
  const get7CStudents = () => {
    const students = [];
    for (let i = 1; i <= 28; i++) {
      students.push({
        id: 600 + i,
        name: `Mustafa ${i}`,
        class: '7-C',
        totalNet: 83 - i * 0.24,
        mathNet: parseFloat((17.5 - i * 0.046).toFixed(1)),
        turkishNet: parseFloat((17.5 - i * 0.042).toFixed(1)),
        scienceNet: parseFloat((17.5 - i * 0.056).toFixed(1)),
        revolutionNet: parseFloat((9.6 - i * 0.026).toFixed(1)),
        religionNet: parseFloat((9.8 - i * 0.023).toFixed(1)),
        englishNet: parseFloat((11.1 - i * 0.032).toFixed(1))
      });
    }
    return students;
  };
  const get6AStudents = () => {
    const students = [];
    for (let i = 1; i <= 30; i++) {
      students.push({
        id: 700 + i,
        name: `Emre ${i}`,
        class: '6-A',
        totalNet: 80 - i * 0.25,
        mathNet: parseFloat((17 - i * 0.05).toFixed(1)),
        turkishNet: parseFloat((17 - i * 0.045).toFixed(1)),
        scienceNet: parseFloat((17 - i * 0.06).toFixed(1)),
        revolutionNet: parseFloat((9.2 - i * 0.025).toFixed(1)),
        religionNet: parseFloat((9.9 - i * 0.026).toFixed(1)),
        englishNet: parseFloat((9.9 - i * 0.03).toFixed(1))
      });
    }
    return students;
  };
  const get6BStudents = () => {
    const students = [];
    for (let i = 1; i <= 32; i++) {
      students.push({
        id: 800 + i,
        name: `Selin ${i}`,
        class: '6-B',
        totalNet: 79 - i * 0.24,
        mathNet: parseFloat((16.5 - i * 0.048).toFixed(1)),
        turkishNet: parseFloat((16.5 - i * 0.043).toFixed(1)),
        scienceNet: parseFloat((16.5 - i * 0.058).toFixed(1)),
        revolutionNet: parseFloat((9.4 - i * 0.024).toFixed(1)),
        religionNet: parseFloat((10.1 - i * 0.025).toFixed(1)),
        englishNet: parseFloat((10 - i * 0.029).toFixed(1))
      });
    }
    return students;
  };
  const get5AStudents = () => {
    const students = [];
    for (let i = 1; i <= 30; i++) {
      students.push({
        id: 900 + i,
        name: `Deniz ${i}`,
        class: '5-A',
        totalNet: 75 - i * 0.23,
        mathNet: parseFloat((16 - i * 0.05).toFixed(1)),
        turkishNet: parseFloat((16 - i * 0.045).toFixed(1)),
        scienceNet: parseFloat((16 - i * 0.06).toFixed(1)),
        revolutionNet: parseFloat((9 - i * 0.023).toFixed(1)),
        religionNet: parseFloat((9.5 - i * 0.024).toFixed(1)),
        englishNet: parseFloat((8.5 - i * 0.028).toFixed(1))
      });
    }
    return students;
  };
  const get5BStudents = () => {
    const students = [];
    for (let i = 1; i <= 32; i++) {
      students.push({
        id: 1000 + i,
        name: `Elif ${i}`,
        class: '5-B',
        totalNet: 74 - i * 0.22,
        mathNet: parseFloat((15.5 - i * 0.048).toFixed(1)),
        turkishNet: parseFloat((15.5 - i * 0.043).toFixed(1)),
        scienceNet: parseFloat((15.5 - i * 0.058).toFixed(1)),
        revolutionNet: parseFloat((9.1 - i * 0.022).toFixed(1)),
        religionNet: parseFloat((9.7 - i * 0.023).toFixed(1)),
        englishNet: parseFloat((8.7 - i * 0.027).toFixed(1))
      });
    }
    return students;
  };
  // Get all students data function
  const getAllStudentsData = () => {
    const allStudents = [...get8AStudents(), ...get8BStudents(), ...get8CStudents(), ...get8DStudents(), ...get7AStudents(), ...get7BStudents(), ...get7CStudents(), ...get6AStudents(), ...get6BStudents(), ...get5AStudents(), ...get5BStudents()];
    return allStudents.sort((a, b) => b.totalNet - a.totalNet);
  };
  // Get students filtered by grade level
  const getStudentsByGradeLevel = gradeLevel => {
    const allStudents = getAllStudentsData();
    return allStudents.filter(student => student.class.startsWith(gradeLevel));
  };
  // Get students for a specific class
  const getClassStudents = className => {
    switch (className) {
      case '8-A':
        return get8AStudents();
      case '8-B':
        return get8BStudents();
      case '8-C':
        return get8CStudents();
      case '8-D':
        return get8DStudents();
      case '7-A':
        return get7AStudents();
      case '7-B':
        return get7BStudents();
      case '7-C':
        return get7CStudents();
      case '6-A':
        return get6AStudents();
      case '6-B':
        return get6BStudents();
      case '5-A':
        return get5AStudents();
      case '5-B':
        return get5BStudents();
      default:
        // For other classes return a small subset
        return get8AStudents().slice(0, 15).map(student => ({
          ...student,
          class: className,
          name: `${className} Öğrenci ${student.id}`
        }));
    }
  };
  // Get exam data by id
  const getExamDataById = examId => {
    const exam = allExamsData.find(exam => exam.id === examId);
    if (!exam) return null;
    // In a real app, this would fetch from an API
    return {
      id: examId,
      name: exam.name,
      date: exam.date,
      institutionAverage: 60 + Math.floor(Math.random() * 15),
      institutionHighest: 75 + Math.floor(Math.random() * 10),
      topStudent: `Öğrenci ${Math.floor(Math.random() * 30) + 1}`,
      topStudentClass: `8-${String.fromCharCode(65 + Math.floor(Math.random() * 4))}`,
      institutionParticipation: 90 + Math.floor(Math.random() * 8),
      institutionParticipants: 145 + Math.floor(Math.random() * 10),
      institutionTotal: 160,
      classes: {
        '8-A': {
          average: 72 + Math.floor(Math.random() * 8),
          highest: 85 + Math.floor(Math.random() * 5),
          topStudent: 'Ahmet 1',
          participation: 96 + Math.floor(Math.random() * 4),
          participants: 29,
          total: 30
        },
        '8-B': {
          average: 70 + Math.floor(Math.random() * 8),
          highest: 84 + Math.floor(Math.random() * 5),
          topStudent: 'Mehmet 1',
          participation: 95 + Math.floor(Math.random() * 5),
          participants: 30,
          total: 32
        },
        '8-C': {
          average: 71 + Math.floor(Math.random() * 8),
          highest: 86 + Math.floor(Math.random() * 4),
          topStudent: 'Ayşe 1',
          participation: 96 + Math.floor(Math.random() * 4),
          participants: 27,
          total: 28
        },
        '8-D': {
          average: 69 + Math.floor(Math.random() * 8),
          highest: 83 + Math.floor(Math.random() * 5),
          topStudent: 'Fatma 1',
          participation: 94 + Math.floor(Math.random() * 6),
          participants: 28,
          total: 30
        },
        '7-A': {
          average: 68 + Math.floor(Math.random() * 7),
          highest: 82 + Math.floor(Math.random() * 4),
          topStudent: 'Zeynep 1',
          participation: 93 + Math.floor(Math.random() * 7),
          participants: 28,
          total: 30
        },
        '7-B': {
          average: 67 + Math.floor(Math.random() * 7),
          highest: 81 + Math.floor(Math.random() * 4),
          topStudent: 'Ali 1',
          participation: 94 + Math.floor(Math.random() * 6),
          participants: 30,
          total: 32
        },
        '7-C': {
          average: 66 + Math.floor(Math.random() * 7),
          highest: 80 + Math.floor(Math.random() * 4),
          topStudent: 'Mustafa 1',
          participation: 92 + Math.floor(Math.random() * 7),
          participants: 26,
          total: 28
        }
      }
    };
  };
  // Get students data for a specific exam
  const getAllStudentsDataForExam = (examId, scope, gradeLevel) => {
    let baseStudents;
    if (scope === 'Kurum Geneli') {
      if (gradeLevel) {
        baseStudents = getStudentsByGradeLevel(gradeLevel);
      } else {
        baseStudents = getAllStudentsData();
      }
    } else {
      baseStudents = getClassStudents(scope);
    }
    if (!examId) return baseStudents;
    // Apply some random variation based on exam ID
    const examIndex = allExamsData.findIndex(exam => exam.id === examId);
    if (examIndex === -1) return baseStudents;
    const examFactor = (examIndex + 1) / allExamsData.length; // 0.125 to 1
    return baseStudents.map(student => ({
      ...student,
      totalNet: parseFloat((student.totalNet * (0.8 + examFactor * 0.2)).toFixed(1)),
      mathNet: parseFloat((student.mathNet * (0.8 + examFactor * 0.2)).toFixed(1)),
      turkishNet: parseFloat((student.turkishNet * (0.8 + examFactor * 0.2)).toFixed(1)),
      scienceNet: parseFloat((student.scienceNet * (0.8 + examFactor * 0.2)).toFixed(1)),
      revolutionNet: parseFloat((student.revolutionNet * (0.8 + examFactor * 0.2)).toFixed(1)),
      religionNet: parseFloat((student.religionNet * (0.8 + examFactor * 0.2)).toFixed(1)),
      englishNet: parseFloat((student.englishNet * (0.8 + examFactor * 0.2)).toFixed(1))
    })).sort((a, b) => b.totalNet - a.totalNet);
  };
  // Update functions
  const updateScopeData = (examId, scope) => {
    const examData = getExamDataById(examId);
    if (!examData) return;
    if (scope === 'Kurum Geneli') {
      setScopeData({
        averageNet: examData.institutionAverage,
        highestNet: examData.institutionHighest,
        topStudent: examData.topStudent,
        topStudentClass: examData.topStudentClass,
        participationRate: examData.institutionParticipation,
        participantCount: examData.institutionParticipants,
        totalStudents: examData.institutionTotal
      });
    } else {
      if (examData.classes[scope]) {
        setScopeData({
          averageNet: examData.classes[scope].average,
          highestNet: examData.classes[scope].highest,
          topStudent: examData.classes[scope].topStudent,
          topStudentClass: scope,
          participationRate: examData.classes[scope].participation,
          participantCount: examData.classes[scope].participants,
          totalStudents: examData.classes[scope].total
        });
      }
    }
  };
  // Update ranking data
  const updateRankingData = (examId, scope, gradeLevel) => {
    const students = getAllStudentsDataForExam(examId, scope, gradeLevel);
    setRankingData(students);
    setCurrentPage(1); // Reset to first page
  };
  // Update development data
  const updateDevelopmentData = examId => {
    // In a real app, this would fetch development data specific to the exam
    // For now, we'll just use our existing data
  };
  // Set initial ranking data
  const [rankingData, setRankingData] = useState([]);
  // Pagination functions
  const totalStudents = rankingData.length;
  const totalPages = Math.ceil(totalStudents / studentsPerPage);
  const getCurrentPageStudents = () => {
    // First filter by search if needed
    const searchFiltered = searchStudent ? rankingData.filter(student => student.name.toLowerCase().includes(searchStudent.toLowerCase())) : rankingData;
    const startIndex = (currentPage - 1) * studentsPerPage;
    const endIndex = startIndex + studentsPerPage;
    return searchFiltered.slice(startIndex, endIndex);
  };
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };
  // Update data when filters change
  useEffect(() => {
    // In a real application, this would fetch data from an API
    // Here we're just simulating data changes
    // Update exam analysis data
    setExamAnalysis({
      averageNet: Math.floor(Math.random() * 20) + 60,
      highestNet: Math.floor(Math.random() * 10) + 80,
      lowestNet: Math.floor(Math.random() * 10) + 50,
      participationRate: Math.floor(Math.random() * 10) + 90 // Random between 90-100
    });
    // Update student data
    const newStudents = students.map(student => ({
      ...student,
      net: Math.floor(Math.random() * 30) + 60,
      correctAnswers: Math.floor(Math.random() * 20) + 70,
      wrongAnswers: Math.floor(Math.random() * 10) + 10 // Random between 10-20
    }));
    // Sort by net score and assign ranks
    newStudents.sort((a, b) => b.net - a.net);
    newStudents.forEach((student, index) => {
      student.rank = index + 1;
    });
    setStudents(newStudents);
    // Update development data
    const newDevelopmentData = developmentData.map(item => ({
      ...item,
      average: Math.floor(Math.random() * 20) + 50 // Random between 50-70
    }));
    setDevelopmentData(newDevelopmentData);
  }, [selectedClass, selectedDate, selectedExam]);
  // Update grade level when scope changes
  useEffect(() => {
    if (selectedScope !== 'Kurum Geneli') {
      // If specific class selected, automatically set grade level
      const autoGrade = selectedScope.charAt(0);
      setSelectedGradeLevel(autoGrade);
    }
  }, [selectedScope]);
  // Update data when scope, exam selection, or grade level changes
  useEffect(() => {
    if (selectedExamId) {
      updateScopeData(selectedExamId, selectedScope);
      updateRankingData(selectedExamId, selectedScope, selectedGradeLevel);
      updateDevelopmentData(selectedExamId);
    }
  }, [selectedScope, selectedExamId, selectedGradeLevel]);
  // Set default exam on component mount
  useEffect(() => {
    // Set the most recent exam as default
    if (allExamsData.length > 0) {
      const mostRecentExam = allExamsData[0]; // First exam is now the most recent
      setSelectedExam(mostRecentExam.name);
      setSelectedExamId(mostRecentExam.id);
      // Initialize ranking data with the default exam
      updateRankingData(mostRecentExam.id, selectedScope, selectedGradeLevel);
    }
  }, []);
  return <div className="space-y-6 bg-black min-h-screen p-6" data-id="element-1961">
      <h1 className="text-2xl font-bold mb-6 text-white" data-id="element-1962">
        Raporlar ve Analizler
      </h1>
      {/* View Selector */}
      <div className="mb-6" data-id="element-1963">
        <div className="p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-green-600 to-emerald-500" data-id="element-1964">
          <div className="bg-black rounded-lg" data-id="element-1965">
            <div className="grid grid-cols-2 gap-1 p-1" data-id="element-1966">
              <button onClick={() => setCurrentView('general')} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all ${currentView === 'general' ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white' : 'bg-black/60 text-gray-400 hover:bg-black/40'}`} data-id="element-1967">
                <BarChart3 size={20} data-id="element-1968" />
                <span data-id="element-1969">Genel Raporlar</span>
              </button>
              <button onClick={() => setCurrentView('exam-analysis')} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all ${currentView === 'exam-analysis' ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white' : 'bg-black/60 text-gray-400 hover:bg-black/40'}`} data-id="element-1970">
                <BookOpenIcon size={20} data-id="element-1971" />
                <span data-id="element-1972">Sınav Analizleri</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {currentView === 'general' ? <>
          {/* Yeni Üst Panel Tasarımı - 3 Dropdown Grid Sistemi */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1973">
            {/* 1. Kapsam Seçimi - 1/3 Genişlik */}
            <div className="relative h-full" data-id="element-1974">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1975">
                Kapsam Seçimi
              </label>
              <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 h-[calc(100%-28px)]" data-id="element-1976">
                <button onClick={() => setShowScopeDropdown(!showScopeDropdown)} className="w-full h-full flex items-center justify-between gap-2 px-6 py-4 bg-black rounded-xl" data-id="element-1977">
                  <span className="text-white font-medium text-base" data-id="element-1978">
                    {selectedScope}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-emerald-400 transition-transform ${showScopeDropdown ? 'rotate-180' : ''}`} data-id="element-1979" />
                </button>
              </div>
              {showScopeDropdown && <div className="absolute top-full left-0 mt-2 z-20 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-xl py-2 max-h-60 overflow-y-auto" data-id="element-1980">
                  {/* Kurum Geneli */}
                  <button onClick={() => handleScopeChange('Kurum Geneli')} className={`w-full text-left px-6 py-3 transition-colors ${selectedScope === 'Kurum Geneli' ? 'bg-emerald-500/20 text-emerald-400' : 'text-white hover:bg-emerald-500/10'}`} data-id="element-1981">
                    <span className="font-medium" data-id="element-1982">Kurum Geneli</span>
                    <div className="text-xs text-gray-400 mt-1" data-id="element-1983">
                      Tüm sınıflar
                    </div>
                  </button>
                  <div className="border-t border-gray-700 my-2" data-id="element-1984"></div>
                  {/* Sınıflar */}
                  {['8-A', '8-B', '8-C', '8-D', '7-A', '7-B', '7-C', '6-A', '6-B', '5-A', '5-B'].map(className => <button key={className} onClick={() => handleScopeChange(className)} className={`w-full text-left px-6 py-3 transition-colors ${selectedScope === className ? 'bg-emerald-500/20 text-emerald-400' : 'text-white hover:bg-emerald-500/10'}`} data-id="element-1985">
                      <span className="font-medium" data-id="element-1986">{className} Sınıfı</span>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-1987">
                        {className.charAt(0)}. Sınıf
                      </div>
                    </button>)}
                </div>}
            </div>
            {/* 2. Deneme Seçimi - 1/3 Genişlik */}
            <div className="relative h-full" data-id="element-1988">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1989">
                Deneme Sınavı
              </label>
              <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 h-[calc(100%-28px)]" data-id="element-1990">
                <button onClick={() => setShowExamDropdown(!showExamDropdown)} className="w-full h-full flex items-center justify-between gap-2 px-6 py-4 bg-black rounded-xl" data-id="element-1991">
                  <div data-id="element-1992">
                    <span className="text-white font-medium text-base block" data-id="element-1993">
                      {selectedExam}
                    </span>
                    <span className="text-xs text-gray-400" data-id="element-1994">
                      {examDates[selectedExamId] || ''}
                    </span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-emerald-400 transition-transform ${showExamDropdown ? 'rotate-180' : ''}`} data-id="element-1995" />
                </button>
              </div>
              {showExamDropdown && <div className="absolute top-full left-0 mt-2 z-20 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-xl py-2 max-h-60 overflow-y-auto" data-id="element-1996">
                  {allExamsData.map(exam => <button key={exam.id} onClick={() => {
              setSelectedExam(exam.name);
              setSelectedExamId(exam.id);
              setShowExamDropdown(false);
            }} className={`w-full text-left px-6 py-3 transition-colors ${selectedExam === exam.name ? 'bg-emerald-500/20 text-emerald-400' : 'text-white hover:bg-emerald-500/10'}`} data-id="element-1997">
                      <span className="font-medium" data-id="element-1998">{exam.name}</span>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-1999">
                        {exam.date}
                      </div>
                    </button>)}
                </div>}
            </div>
            {/* 3. Seviye Seçimi - 1/3 Genişlik (Koşullu) */}
            {shouldShowGradeDropdown ? <div className="relative h-full" data-id="element-2000">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2001">
                  Sınıf Seviyesi
                </label>
                <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 h-[calc(100%-28px)]" data-id="element-2002">
                  <button onClick={() => setShowGradeDropdown(!showGradeDropdown)} className="w-full h-full flex items-center justify-between gap-2 px-6 py-4 bg-black rounded-xl" data-id="element-2003">
                    <span className="text-white font-medium text-base" data-id="element-2004">
                      {selectedGradeLevel}. Sınıf
                    </span>
                    <ChevronDown className={`w-5 h-5 text-emerald-400 transition-transform ${showGradeDropdown ? 'rotate-180' : ''}`} data-id="element-2005" />
                  </button>
                </div>
                {showGradeDropdown && <div className="absolute top-full left-0 mt-2 z-20 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-xl py-2" data-id="element-2006">
                    {['8', '7', '6', '5'].map(grade => <button key={grade} onClick={() => {
              setSelectedGradeLevel(grade);
              setShowGradeDropdown(false);
            }} className={`w-full text-left px-6 py-3 transition-colors ${selectedGradeLevel === grade ? 'bg-emerald-500/20 text-emerald-400' : 'text-white hover:bg-emerald-500/10'}`} data-id="element-2007">
                        <span className="font-medium" data-id="element-2008">{grade}. Sınıf</span>
                        <div className="text-xs text-gray-400 mt-1" data-id="element-2009">
                          {grade === '8' ? '8-A, 8-B, 8-C, 8-D' : grade === '7' ? '7-A, 7-B, 7-C' : grade === '6' ? '6-A, 6-B' : '5-A, 5-B'}
                        </div>
                      </button>)}
                  </div>}
              </div> : <div className="relative h-full" data-id="element-2010">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2011">
                  Sınıf Seviyesi
                </label>
                <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-gray-700 to-gray-600 h-[calc(100%-28px)]" data-id="element-2012">
                  <div className="w-full h-full px-6 py-4 bg-black rounded-xl cursor-not-allowed" data-id="element-2013">
                    <span className="text-gray-400 font-medium text-base" data-id="element-2014">
                      {selectedScope.charAt(0)}. Sınıf
                    </span>
                    <div className="text-xs text-gray-500 mt-1" data-id="element-2015">
                      Otomatik belirlendi
                    </div>
                  </div>
                </div>
              </div>}
          </div>
          {/* Seçim Özeti */}
          <div className="mb-6 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20" data-id="element-2016">
            <div className="text-sm text-emerald-400 font-medium" data-id="element-2017">
              Aktif Seçim: {selectedScope} • {selectedExam}
              {shouldShowGradeDropdown && ` • ${selectedGradeLevel}. Sınıf Seviyesi`}
            </div>
          </div>
          {/* Genel Analiz Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2018">
            {/* Ortalama Net Kartı */}
            <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-2019">
              <div className="p-6 bg-black/90 rounded-xl" data-id="element-2020">
                <div className="flex items-center justify-between mb-4" data-id="element-2021">
                  <h3 className="text-lg font-medium text-gray-300" data-id="element-2022">
                    Ortalama Net
                  </h3>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center" data-id="element-2023">
                    <BarChart className="w-4 h-4 text-white" data-id="element-2024" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-emerald-400 mb-2" data-id="element-2025">
                  {scopeData.averageNet}/90
                </div>
                <div className="text-sm text-gray-400" data-id="element-2026">
                  {selectedScope === 'Kurum Geneli' ? 'Kurum ortalaması' : `${selectedScope} ortalaması`}
                </div>
              </div>
            </div>
            {/* En Yüksek Net Kartı */}
            <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-2027">
              <div className="p-6 bg-black/90 rounded-xl" data-id="element-2028">
                <div className="flex items-center justify-between mb-4" data-id="element-2029">
                  <h3 className="text-lg font-medium text-gray-300" data-id="element-2030">
                    En Yüksek Net
                  </h3>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center" data-id="element-2031">
                    <TrendingUp className="w-4 h-4 text-white" data-id="element-2032" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-emerald-400 mb-2" data-id="element-2033">
                  {scopeData.highestNet}/90
                </div>
                <div className="text-sm text-gray-400" data-id="element-2034">
                  {scopeData.topStudent && `${scopeData.topStudent} (${scopeData.topStudentClass})`}
                </div>
              </div>
            </div>
            {/* Katılım Oranı Kartı */}
            <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-2035">
              <div className="p-6 bg-black/90 rounded-xl" data-id="element-2036">
                <div className="flex items-center justify-between mb-4" data-id="element-2037">
                  <h3 className="text-lg font-medium text-gray-300" data-id="element-2038">
                    Katılım Oranı
                  </h3>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center" data-id="element-2039">
                    <Users className="w-4 h-4 text-white" data-id="element-2040" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-emerald-400 mb-2" data-id="element-2041">
                  %{scopeData.participationRate}
                </div>
                <div className="text-sm text-gray-400" data-id="element-2042">
                  {scopeData.participantCount}/{scopeData.totalStudents} öğrenci
                </div>
              </div>
            </div>
          </div>
          {/* Sıralama Tablosu - "Toplam Net" butonu kaldırıldı */}
          <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 mb-8" data-id="element-2043">
            <div className="p-6 bg-black/90 rounded-xl" data-id="element-2044">
              {/* Sadece başlık ve arama */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6" data-id="element-2045">
                <h2 className="text-xl font-bold" data-id="element-2046">
                  {selectedScope === 'Kurum Geneli' ? `${selectedGradeLevel}. Sınıf Seviyesi Sıralaması` : `${selectedScope} Sınıf Sıralaması`}
                </h2>
                {/* Öğrenci Arama */}
                <div className="relative w-full md:w-64" data-id="element-2047">
                  <input type="text" placeholder="Öğrenci ara..." value={searchStudent} onChange={e => setSearchStudent(e.target.value)} className="w-full px-4 py-2 pl-10 bg-white/5 text-white rounded-lg border border-emerald-500/30 focus:border-emerald-500/50 transition-colors" data-id="element-2048" />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2049" />
                </div>
              </div>
              {/* Sıralama Tablosu */}
              <div className="overflow-x-auto" data-id="element-2050">
                <table className="w-full" data-id="element-2051">
                  <thead data-id="element-2052">
                    <tr className="border-b border-gray-800" data-id="element-2053">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400" data-id="element-2054">
                        Sıra
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400" data-id="element-2055">
                        {selectedScope === 'Kurum Geneli' ? 'Öğrenci / Sınıf' : 'Öğrenci'}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-400" data-id="element-2056">
                        Toplam Net
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-400" data-id="element-2057">
                        Matematik
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-400" data-id="element-2058">
                        Türkçe
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-400" data-id="element-2059">
                        Fen
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-400" data-id="element-2060">
                        İnkılap
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-400" data-id="element-2061">
                        Din
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-400" data-id="element-2062">
                        İngilizce
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2063">
                    {getCurrentPageStudents().map((student, index) => {
                  const actualRank = (currentPage - 1) * studentsPerPage + index + 1;
                  return <tr key={student.id} className="border-b border-gray-800 hover:bg-white/5 transition-colors" data-id="element-2064">
                          <td className="px-4 py-3" data-id="element-2065">
                            <div className="flex items-center gap-2" data-id="element-2066">
                              {actualRank <= 3 ? <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${actualRank === 1 ? 'bg-yellow-500 text-black' : actualRank === 2 ? 'bg-gray-400 text-black' : 'bg-amber-600 text-white'}`} data-id="element-2067">
                                  {actualRank}
                                </div> : <span className="w-6 text-center text-gray-400" data-id="element-2068">
                                  {actualRank}
                                </span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-2069">
                            <div className="flex items-center gap-3" data-id="element-2070">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center" data-id="element-2071">
                                <span className="text-white font-bold text-sm" data-id="element-2072">
                                  {student.name[0]}
                                </span>
                              </div>
                              <div data-id="element-2073">
                                <div className="text-sm font-medium text-white" data-id="element-2074">
                                  {student.name}
                                </div>
                                {selectedScope === 'Kurum Geneli' && <div className="text-xs text-gray-400" data-id="element-2075">
                                    {student.class}
                                  </div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-2076">
                            <span className="text-lg font-bold text-emerald-400" data-id="element-2077">
                              {student.totalNet}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-blue-500" data-id="element-2078">
                            {student.mathNet}
                          </td>
                          <td className="px-4 py-3 text-center text-red-600" data-id="element-2079">
                            {student.turkishNet}
                          </td>
                          <td className="px-4 py-3 text-center text-emerald-600" data-id="element-2080">
                            {student.scienceNet}
                          </td>
                          <td className="px-4 py-3 text-center text-amber-600" data-id="element-2081">
                            {student.revolutionNet}
                          </td>
                          <td className="px-4 py-3 text-center text-blue-900" data-id="element-2082">
                            {student.religionNet}
                          </td>
                          <td className="px-4 py-3 text-center text-purple-500" data-id="element-2083">
                            {student.englishNet}
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
              {/* Sayfalama Kontrolü */}
              {totalPages > 1 && <div className="flex flex-col md:flex-row items-center justify-between mt-6" data-id="element-2084">
                  <div className="text-sm text-gray-400 mb-2 md:mb-0" data-id="element-2085">
                    {rankingData.length > 0 ? <>
                        {(currentPage - 1) * studentsPerPage + 1}-
                        {Math.min(currentPage * studentsPerPage, totalStudents)}{' '}
                        / {totalStudents} öğrenci
                      </> : 'Sonuç bulunamadı'}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2086">
                    <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className={`px-3 py-2 rounded-lg text-sm transition-colors ${currentPage === 1 ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-white/5 text-white hover:bg-white/10'}`} data-id="element-2087">
                      Önceki
                    </button>
                    {/* Sayfa Numaraları */}
                    <div className="flex gap-1" data-id="element-2088">
                      {getPageNumbers().map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 rounded-lg text-sm transition-colors ${currentPage === page ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white' : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'}`} data-id="element-2089">
                          {page}
                        </button>)}
                    </div>
                    <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className={`px-3 py-2 rounded-lg text-sm transition-colors ${currentPage === totalPages ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-white/5 text-white hover:bg-white/10'}`} data-id="element-2090">
                      Sonraki
                    </button>
                  </div>
                </div>}
            </div>
          </div>
          {/* Sınıfların Gelişim Grafiği */}
          <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-2091">
            <div className="p-6 bg-black/90 rounded-xl" data-id="element-2092">
              <h2 className="text-xl font-bold mb-6" data-id="element-2093">
                {selectedScope === 'Kurum Geneli' ? `${selectedGradeLevel}. Sınıf Seviyesi Gelişim Grafiği` : `${selectedScope} Gelişim Trendi`}
              </h2>
              <div className="h-80" data-id="element-2094">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2095">
                  <LineChart data={newDevelopmentData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-2096">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" data-id="element-2097" />
                    <XAxis dataKey="examName" stroke="#999" tick={{
                  fontSize: 12
                }} data-id="element-2098" />
                    <YAxis stroke="#999" domain={[0, 90]} ticks={[0, 15, 30, 45, 60, 75, 90]} tick={{
                  fontSize: 12
                }} data-id="element-2099" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }} formatter={(value, name) => [`${value} net`, name === 'average' ? 'Ortalama' : name === '8A' ? '8-A Sınıfı' : name === '8B' ? '8-B Sınıfı' : name === '8C' ? '8-C Sınıfı' : name]} labelFormatter={label => `Deneme: ${label}`} data-id="element-2100" />
                    <Legend wrapperStyle={{
                  color: '#fff'
                }} formatter={value => value === 'average' ? 'Kurum Ortalaması' : value === '8A' ? '8-A Sınıfı' : value === '8B' ? '8-B Sınıfı' : value === '8C' ? '8-C Sınıfı' : value} data-id="element-2101" />
                    {/* Ana çizgiler */}
                    <Line type="monotone" dataKey="average" name="average" stroke="#10b981" strokeWidth={3} dot={{
                  r: 6,
                  fill: '#10b981'
                }} activeDot={{
                  r: 8,
                  fill: '#10b981'
                }} data-id="element-2102" />
                    {selectedScope === 'Kurum Geneli' && selectedGradeLevel === '8' && <>
                          <Line type="monotone" dataKey="8A" name="8A" stroke="#3b82f6" strokeWidth={2} dot={{
                    r: 4,
                    fill: '#3b82f6'
                  }} strokeDasharray="5 5" data-id="element-2103" />
                          <Line type="monotone" dataKey="8B" name="8B" stroke="#ef4444" strokeWidth={2} dot={{
                    r: 4,
                    fill: '#ef4444'
                  }} strokeDasharray="5 5" data-id="element-2104" />
                          <Line type="monotone" dataKey="8C" name="8C" stroke="#f59e0b" strokeWidth={2} dot={{
                    r: 4,
                    fill: '#f59e0b'
                  }} strokeDasharray="5 5" data-id="element-2105" />
                        </>}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </> : <ExamAnalysis data-id="element-2106" />}
    </div>;
};
export default Reports;